import { memo } from "react";
import clsx from "clsx";
import type { POI } from "@poi-planner/shared";
import { formatTimeBudget, formatDistance } from "../utils/format";
import { hasUsableCoordinates } from "../utils/dayMapPois";
import { useLibraryStore } from "../store/libraryStore";
import { resolveTagNames } from "../utils/tagDisplay";
import { Button } from "./Button";
import { PoiDetails } from "./PoiDetails";

interface SidebarPoiCardProps {
  poi: POI;
  // Visual states
  isSelected?: boolean;
  isTargeted?: boolean;
  isDraggable?: boolean;
  /** Show notes, sources, and the Maps link below the tags. */
  isExpanded?: boolean;
  // Badges
  scheduledCount?: number;
  distance?: number;
  showDistance?: boolean;
  // Pin state (trip context)
  isPinned?: boolean;
  isScheduled?: boolean;
  hasLegs?: boolean;
  onTogglePin?: (poiId: string) => Promise<string | null>;
  onShowPinMessage?: (message: string) => void;
  // Callbacks
  onClick?: () => void;
  onEdit?: () => void;
  /** Whether onEdit opens a saveable editor (true) or a read-only viewer (false). Defaults true. */
  canEdit?: boolean;
  onSetTarget?: (poiId: string | null) => void;
}

function SidebarPoiCardComponent({
  poi,
  isSelected = false,
  isTargeted = false,
  isDraggable = false,
  isExpanded = false,
  scheduledCount = 0,
  distance,
  showDistance = false,
  isPinned = false,
  isScheduled = false,
  hasLegs = false,
  onTogglePin,
  onShowPinMessage,
  onClick,
  onEdit,
  canEdit = true,
  onSetTarget,
}: SidebarPoiCardProps) {
  const managedTags = useLibraryStore((state) => state.tags);
  const tagNames = resolveTagNames(poi.tags, managedTags);
  const hasCoords = hasUsableCoordinates(poi);
  const hasNotes = !!poi.notes?.trim();
  const sourceCount = poi.sources.filter((s) => s.url?.trim()).length;

  return (
    <div
      data-poi-id={poi.id}
      onClick={onClick}
      className={clsx(
        "group p-3 rounded-xl border cursor-pointer transition-all",
        "hover:shadow-md",
        isSelected
          ? "border-blue-500 bg-blue-50 shadow-sm"
          : "border-border bg-white hover:border-slate-300",
      )}
    >
      <span
        title={poi.name}
        className={clsx(
          "text-sm font-bold block line-clamp-2 break-words",
          isSelected ? "text-blue-700" : "text-primary",
        )}
      >
        {poi.name}
      </span>
      {poi.ownerName && (
        <span className="text-2xs font-medium text-tertiary block truncate">
          {poi.ownerName}
        </span>
      )}

      <div className="flex flex-wrap items-center gap-x-1 gap-y-1 mt-1 min-h-6 whitespace-nowrap">
        <div className="flex items-center gap-1 text-secondary text-xs">
          <span className="material-symbols-outlined text-sm">schedule</span>
          <span>{formatTimeBudget(poi.timeBudget)}</span>
        </div>
        {/* Distance badge */}
        {showDistance &&
          (distance !== undefined ? (
            <span className="text-xs font-bold px-1.5 py-px rounded bg-blue-500/10 text-blue-500">
              {formatDistance(distance)}
            </span>
          ) : (
            <span className="text-xs font-semibold px-1.5 py-px rounded bg-subtle text-gray-400 italic">
              No location
            </span>
          ))}
        {/* No location badge (when not showing distance) */}
        {!showDistance && !hasCoords && (
          <span
            className="text-xs font-semibold px-1.5 py-px rounded bg-amber-100 text-amber-700"
            title="No coordinates"
          >
            No location
          </span>
        )}
        {/* Scheduled count badge */}
        {scheduledCount > 0 && (
          <span className="text-xs font-bold px-1.5 py-px rounded bg-blue-100 text-blue-800">
            {scheduledCount}x
          </span>
        )}
        {/* Hints that a compact card has more to show */}
        {!isExpanded && (hasNotes || sourceCount > 0) && (
          <span className="flex items-center gap-1.5 text-tertiary text-xs">
            {hasNotes && (
              <span
                className="material-symbols-outlined text-sm"
                title="Has notes"
                aria-label="Has notes"
              >
                notes
              </span>
            )}
            {sourceCount > 0 && (
              <span
                className="flex items-center gap-0.5"
                title={`${sourceCount} ${sourceCount === 1 ? "source" : "sources"}`}
                aria-label={`${sourceCount} ${sourceCount === 1 ? "source" : "sources"}`}
              >
                <span className="material-symbols-outlined text-sm" aria-hidden>
                  bookmark
                </span>
                {sourceCount}
              </span>
            )}
          </span>
        )}
        <div className="flex items-center gap-0.5 ml-auto">
          {/* Target button */}
          {hasCoords && onSetTarget && (
            <Button
              onClick={(e) => {
                e.stopPropagation();
                onSetTarget(isTargeted ? null : poi.id);
              }}
              title={
                isTargeted
                  ? "Clear proximity target"
                  : "Sort by distance from this POI"
              }
              aria-label={
                isTargeted
                  ? "Clear proximity target"
                  : `Sort by distance from ${poi.name}`
              }
              variant="unstyled"
              size="sm"
              icon="gps_fixed"
              selected={isTargeted}
              className={clsx(
                "flex items-center justify-center size-6 rounded-md transition-all",
                isTargeted
                  ? "bg-blue-500 text-white"
                  : "text-secondary reveal-on-card-hover hover:bg-blue-500/10 hover:text-blue-500",
              )}
            />
          )}
          {/* Edit/view button */}
          {onEdit && (
            <Button
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              title={canEdit ? "Edit POI" : "View POI"}
              aria-label={canEdit ? `Edit ${poi.name}` : `View ${poi.name}`}
              variant="unstyled"
              size="sm"
              icon={canEdit ? "edit" : "visibility"}
              className={clsx(
                "flex items-center justify-center size-6 rounded-md transition-all",
                isSelected
                  ? "hover:bg-blue-200 text-blue-600"
                  : "text-secondary reveal-on-card-hover hover:bg-slate-100",
              )}
            />
          )}
          {/* Pin button (trip context only) */}
          {onTogglePin && (
            <Button
              onClick={(e) => {
                e.stopPropagation();
                void onTogglePin(poi.id).then((message) => {
                  if (message) onShowPinMessage?.(message);
                });
              }}
              title={
                isPinned && isScheduled && !hasLegs
                  ? "Remove from day first before unpinning"
                  : isPinned && hasLegs
                    ? "Manage pin"
                    : isPinned
                      ? "Remove pin"
                      : "Pin to trip"
              }
              aria-label={
                isPinned && hasLegs
                  ? `Manage pin for ${poi.name}`
                  : isPinned
                    ? `Remove pin from ${poi.name}`
                    : `Pin ${poi.name} to trip`
              }
              variant="unstyled"
              size="sm"
              icon={isPinned ? "keep" : "keep_off"}
              selected={isPinned}
              className={clsx(
                "flex items-center justify-center size-6 rounded-md transition-all",
                isPinned
                  ? "text-purple-600 opacity-100"
                  : "text-secondary reveal-on-card-hover hover:bg-purple-500/10 hover:text-purple-500",
              )}
            />
          )}
          {/* Drag indicator */}
          {isDraggable && (
            <span className="material-symbols-outlined text-gray-300 text-lg">
              drag_indicator
            </span>
          )}
        </div>
      </div>

      {tagNames.length > 0 && (
        <div className="flex gap-1 flex-wrap mt-2">
          {tagNames.map((tag) => (
            <span
              key={tag}
              className={clsx(
                "text-xs py-0.5 px-1.5 rounded-md font-medium",
                isSelected
                  ? "bg-blue-200 text-blue-700"
                  : "bg-slate-100 text-secondary",
              )}
            >
              {tag}
            </span>
          ))}
        </div>
      )}

      {isExpanded && (
        <PoiDetails
          poi={poi}
          // Only the selected card prompts for missing notes, so expanding
          // every card doesn't repeat the prompt down the list.
          canEdit={canEdit && isSelected}
          onEdit={onEdit}
          className={clsx(
            "mt-2.5 border-t pt-2.5",
            isSelected ? "border-blue-200" : "border-border",
          )}
        />
      )}
    </div>
  );
}

export const SidebarPoiCard = memo(SidebarPoiCardComponent);
SidebarPoiCard.displayName = "SidebarPoiCard";
